import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useCoachColour } from "@/design/use-coach-colour";
import { PortalView } from "@/pages/portal/portal-view";
import type {
  PortalData,
  PortalInvoice,
  PortalSubscription,
} from "@/data/portal";

/**
 * `/dev/portal`, mounted only under `import.meta.env.DEV`.
 *
 * The signed-in portal cannot be reached without a magic-link token, and a token cannot be
 * minted without sending a real email to a real client. So the states that matter are rendered
 * here on a fixture instead: a subscription inside a commitment beside one that is already
 * cancelling, two cards, and four invoices in four states. The cancel modal and the add-card
 * region open from the URL (`?modal=cancel`, `?add=1`) so a screenshot can catch them.
 *
 * It is a fixture, not a story: it renders the real `PortalView` with the real types, so a
 * change to the page that breaks a state breaks this too. `?coach=RRGGBB` hands the action over
 * to a coach colour, exactly as a real payload does.
 */

const DAY = 24 * 60 * 60 * 1000;
const inDays = (days: number) =>
  new Date(Date.now() + days * DAY).toISOString();

const SUBSCRIPTIONS: PortalSubscription[] = [
  {
    id: "sub_committed",
    productName: "Coaching 1 to 1",
    status: "active",
    currentPeriodStart: inDays(-12),
    currentPeriodEnd: inDays(18),
    cancelAtPeriodEnd: false,
    commitmentEndDate: inDays(96),
    price: {
      amount: 7900,
      currency: "eur",
      interval: "month",
      intervalCount: 1,
    },
  },
  {
    id: "sub_cancelling",
    productName: "Nutrition follow-up",
    status: "active",
    currentPeriodStart: inDays(-3),
    currentPeriodEnd: inDays(27),
    cancelAtPeriodEnd: true,
    canceledAt: inDays(-1),
    price: {
      amount: 3500,
      currency: "eur",
      interval: "month",
      intervalCount: 1,
    },
  },
];

const DATA: PortalData = {
  customer: {
    id: "cus_fixture",
    email: "lea.martin@example.com",
    name: "Léa Martin",
  },
  business: {
    name: "Mehdi Benali",
    supportEmail: "mehdi@example.com",
  },
  subscriptions: SUBSCRIPTIONS,
  paymentMethods: [
    {
      id: "pm_default",
      type: "card",
      isDefault: true,
      card: { brand: "visa", last4: "4417", expMonth: 8, expYear: 2028 },
    },
    {
      id: "pm_other",
      type: "card",
      isDefault: false,
      card: { brand: "mastercard", last4: "9021", expMonth: 11, expYear: 2027 },
    },
  ],
  stripeAccountId: "acct_fixture",
};

const INVOICES: PortalInvoice[] = [
  {
    id: "in_paid",
    number: "2026-0142",
    status: "paid",
    currency: "eur",
    amountDue: 0,
    amountPaid: 7900,
    total: 7900,
    createdOn: inDays(-6),
    paidAt: inDays(-6),
    invoicePdf: "https://example.invalid/invoice-2026-0142.pdf",
  },
  {
    id: "in_open",
    number: "2026-0151",
    status: "open",
    currency: "eur",
    amountDue: 7900,
    amountPaid: 0,
    total: 7900,
    createdOn: inDays(-2),
    dueDate: inDays(5),
    invoicePdf: "https://example.invalid/invoice-2026-0151.pdf",
  },
  {
    id: "in_uncollectible_9f2a1c",
    status: "uncollectible",
    currency: "eur",
    amountDue: 3500,
    amountPaid: 0,
    total: 3500,
    createdOn: inDays(-64),
  },
  {
    id: "in_void",
    number: "2026-0118",
    status: "void",
    currency: "eur",
    amountDue: 0,
    amountPaid: 0,
    total: 7900,
    createdOn: inDays(-37),
    invoicePdf: "https://example.invalid/invoice-2026-0118.pdf",
  },
];

export function PortalFixture() {
  const { i18n } = useTranslation();
  const params = new URLSearchParams(window.location.search);
  /* `?coach=RRGGBB` here too, so the same three colours can be shot on the portal. The fixture
     carries no brandColor of its own; the override is the whole point. */
  const coachHex = useCoachColour(null);
  const [addingCard, setAddingCard] = useState(params.get("add") === "1");
  const [cancelTarget, setCancelTarget] = useState<PortalSubscription | null>(
    params.get("modal") === "cancel" ? SUBSCRIPTIONS[1] : null,
  );

  return (
    <PortalView
      data={DATA}
      invoices={INVOICES}
      stripe={null}
      coachHex={coachHex}
      language={i18n.resolvedLanguage ?? i18n.language}
      addingCard={addingCard}
      /* No Stripe instance and no secret here: the add-card region draws its frame and its two
         buttons, and the card fields themselves belong to Stripe and are not faked. */
      setupClientSecret={null}
      settingDefaultId={null}
      cardsError={null}
      onOpenAdd={() => setAddingCard(true)}
      onCloseAdd={() => setAddingCard(false)}
      onCardAdded={() => setAddingCard(false)}
      onSetDefault={() => undefined}
      cancelTarget={cancelTarget}
      cancelling={false}
      cancelError={null}
      onAskCancel={setCancelTarget}
      onCloseCancel={() => setCancelTarget(null)}
      onConfirmCancel={() => setCancelTarget(null)}
    />
  );
}
